import { useTranslation } from "react-i18next";
import { FormEvent, useContext, useEffect, useRef, useState } from "react";

import {
  Form,
  FormContext,
  FormErrorTypes,
  Input,
  InputNumber,
  InputPercentage,
  Flex,
  Button,
  Text,
} from "dash-ui";
import { useTheme } from "styled-components";

import useValidation from "pages/Dashboard/components/MortgageCalculatorWidget/components/MortgageForm/hooks/useValidation";
import { FormFields } from "pages/Dashboard/components/MortgageCalculatorWidget/components/MortgageForm/constants/constants";

const MortgageForm = ({ onSubmit }: { onSubmit?: () => void }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const theme = useTheme();
  const { t } = useTranslation(["forms", "common"]);
  const [isShowMore, setIsShowMore] = useState(false);
  const {
    formState,
    formOnSubmit,
    resetFormErrors,
    formErrors,
    setFormValues,
    stateHandlers,
  } = useContext(FormContext);
  const validateFieldByName = useValidation();

  useEffect(() => {
    setFormValues({
      taxChecked: true,
      insuranceChecked: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitSpy = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      !validateFieldByName(FormFields.TotalPrice, formState.totalPrice) ||
      !validateFieldByName(FormFields.DownPayment, formState.downPayment) ||
      !validateFieldByName(FormFields.InterestRate, formState.interestRate) ||
      !validateFieldByName(FormFields.Term, formState.term)
    ) {
      return null;
    }
    formOnSubmit();
    resetFormErrors();

    if (onSubmit) {
      onSubmit();
    }
    stateHandlers[FormFields.LastRecalculationMoment](Date.now());
  };

  const onPriceChanged = (type: FormFields) => (value: number) => {
    stateHandlers[type](value);
    validateFieldByName(type, value);
  };

  return (
    <Form
      id="mortgage-form"
      className="mortgage-form w-full"
      ref={formRef}
      onSubmit={onSubmitSpy}
    >
      <Form.Group w="100%" mt={4}>
        <Flex flex={1}>
          <Flex flex={2} flexDirection="column" mr={3}>
            <Form.Label>{t("forms:labels.mortgageTotalPrice")}</Form.Label>
            <InputNumber
              value={formState.totalPrice || ""}
              w="100%"
              placeholder={t("forms:placeholders.mortgageTotalPrice") || ""}
              onChangeValue={onPriceChanged(FormFields.TotalPrice)}
              $errorType={formErrors[FormFields.TotalPrice]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.TotalPrice]?.type}>
              {formErrors[FormFields.TotalPrice]?.message}
            </Form.Feedback>
          </Flex>
          <Flex flex={1} minWidth={96} flexDirection="column">
            <Form.Label>{t("forms:labels:mortgageInterestRate")}</Form.Label>
            <InputPercentage
              value={formState.interestRate || ""}
              w="100%"
              placeholder={t("forms:placeholders:mortgageInterestRate") || ""}
              onChangeValue={onPriceChanged(FormFields.InterestRate)}
              $errorType={formErrors[FormFields.InterestRate]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.InterestRate]?.type}
            >
              {formErrors[FormFields.InterestRate]?.message}
            </Form.Feedback>
          </Flex>
        </Flex>
      </Form.Group>
      <Form.Group w="100%" mt={4}>
        <Flex flex={1}>
          <Flex flex={1} flexDirection="column" mr={3}>
            <Form.Label>{t("forms:labels:mortgageDownPayment")}</Form.Label>
            <InputPercentage
              value={formState.downPayment || ""}
              w="100%"
              placeholder={t("forms:placeholders.mortgageDownPayment") || ""}
              onChangeValue={onPriceChanged(FormFields.DownPayment)}
              $errorType={formErrors[FormFields.DownPayment]?.type}
            />
            <Form.Feedback
              $errorType={formErrors[FormFields.DownPayment]?.type}
            >
              {formErrors[FormFields.DownPayment]?.message}
            </Form.Feedback>
          </Flex>
          <Flex flex={1} flexDirection="column">
            <Form.Label>{t("forms:labels.mortgageTerm")}</Form.Label>
            <Input
              value={formState.term || ""}
              w="100%"
              type="number"
              placeholder={t("forms:placeholders.mortgageTerm") || ""}
              onChange={(e) => {
                const value = e.target.value;
                stateHandlers[FormFields.Term](value);
                validateFieldByName(FormFields.Term, value);
              }}
              $errorType={formErrors[FormFields.Term]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.Term]?.type}>
              {formErrors[FormFields.Term]?.message}
            </Form.Feedback>
          </Flex>
        </Flex>
      </Form.Group>
      <Form.Group mt={4}>
        <Button
          px={0}
          h={16}
          type="button"
          variation="flat"
          iconRight={isShowMore ? "chevronUp" : "chevronDown"}
          iconRightProps={{
            size: 20,
            color: theme.colors.g20,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsShowMore(!isShowMore);
          }}
        >
          <Text as="span" color={theme.colors.g20}>
            {isShowMore
              ? t("common:labels:lessOptions")
              : t("common:labels:moreOptions")}
          </Text>
        </Button>
      </Form.Group>
      {isShowMore && (
        <Form.Group w="100%" mt={4}>
          <Flex flex={1}>
            <Flex flex={1} flexDirection="column" mr={3}>
              <Form.Label required>
                {t("forms:labels.mortgageTaxRate")}
              </Form.Label>
              <InputNumber
                value={formState.taxRate || ""}
                w="100%"
                placeholder={t("forms:placeholders.mortgageTaxRate") || ""}
                onChangeValue={(value) =>
                  stateHandlers[FormFields.TaxRate](value)
                }
                $errorType={formErrors[FormFields.TaxRate]?.type}
              />
              <Form.Feedback $errorType={formErrors[FormFields.TaxRate]?.type}>
                {formErrors[FormFields.TaxRate]?.message}
              </Form.Feedback>
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Form.Label required>
                {t("forms:labels.mortgageInsuranceRate")}
              </Form.Label>
              <InputNumber
                value={formState.insuranceRate || ""}
                w="100%"
                placeholder={
                  t("forms:placeholders.mortgageInsuranceRate") || ""
                }
                onChangeValue={(value) =>
                  stateHandlers[FormFields.InsuranceRate](value)
                }
                $errorType={formErrors[FormFields.InsuranceRate]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[FormFields.InsuranceRate]?.type}
              >
                {formErrors[FormFields.InsuranceRate]?.message}
              </Form.Feedback>
            </Flex>
          </Flex>
        </Form.Group>
      )}
      <Form.Group mt={4}>
        <Button w="100%" type="submit">
          {t("common:labels:calculate")}
        </Button>
      </Form.Group>
      <Flex mt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Flex>
    </Form>
  );
};

export default MortgageForm;
